import { useState } from "react";
import { useNavigate } from "react-router-dom";
import moment from "moment/moment";
import { IconLogout, IconPlus } from "@tabler/icons-react";
import Button from "../../UI/Button";
import Card from "../../UI/Card";
import CountBadge from "../../UI/CountBadge";
import CollapsibleCard from "../../UI/CollapsibleCard";
import Text from "../../UI/Text";
import Title from "../../UI/Title.tsx";
import PriorityActionItem from "./PriorityActionItem";
import PriorityActionForm from "./PriorityActionForm";
import SnoozeDialog from "./SnoozeDialog";
import ArchivedActionsDialog from "./ArchivedActionsDialog";
import usePriorityActions from "../../../hooks/use-priority-actions";
import type { PriorityAction } from "../../../models/priorityAction";
import type { Tenant } from "../../../models/tenant";
// @ts-expect-error pure js
import { ProcessType } from "../../../utils/constants/processes";
import { BalanceDomain } from "../../../utils/constants/balances";
import {
  CATEGORIES,
  CATEGORY_ORDER,
  TYPE_CONFIG,
  isSnoozed,
} from "./priorityActionConfig";
import styles from "./PriorityActions.module.css";

interface Process {
  id: number | string;
  id_tenant: number | string;
  type: string;
  is_active: boolean;
  entries?: Record<string, boolean | undefined>;
}

type StepDef = {
  title: string;
  done: (process: Process, tenant: Tenant) => boolean;
};

const COMMON_STEPS: StepDef[] = [
  {
    title: "Rappel de fin de bail",
    done: (p) => !!p.entries?.rappel_fin_bail,
  },
  {
    title: "Convocation état des lieux",
    done: (p) => !!p.entries?.convocation_edl,
  },
  { title: "Réaliser l'état des lieux", done: (p) => !!p.entries?.edl },
  { title: "Dépôt de garantie", done: (p) => !!p.entries?.dg },
  {
    title: "Affectation du virement du dépôt de garantie",
    done: (p) => !!p.entries?.affectation_dg,
  },
];

const STEPS_BY_TYPE: Record<string, StepDef[]> = {
  [ProcessType.DEPART]: [
    { title: "Réception du préavis", done: (_p, t) => !!t.preavis },
    ...COMMON_STEPS,
  ],
  [ProcessType.DEPART_BAILLEUR]: [
    { title: "Envoyer le congé", done: (p) => !!p.entries?.envoie_conge },
    {
      title: "Accusé réception du courrier",
      done: (p) => !!p.entries?.ar_conge,
    },
    ...COMMON_STEPS,
  ],
};

const PROCESS_LABEL: Record<string, string> = {
  [ProcessType.DEPART]: "Départ initié par le locataire",
  [ProcessType.DEPART_BAILLEUR]: "Départ initié par le bailleur",
};

function getCurrentStep(process: Process, tenant: Tenant) {
  const steps = STEPS_BY_TYPE[process.type];
  if (!steps) return null;
  const currentIndex = steps.findIndex((step) => !step.done(process, tenant));
  if (currentIndex === -1) {
    return {
      index: steps.length,
      total: steps.length,
      title: "Prêt à clôturer",
    };
  }
  return {
    index: currentIndex + 1,
    total: steps.length,
    title: steps[currentIndex].title,
  };
}

function getProcessSubtitle(process: Process, tenant: Tenant | undefined) {
  const typeLabel = PROCESS_LABEL[process.type] ?? process.type;
  const step = tenant ? getCurrentStep(process, tenant) : null;
  return step
    ? `${typeLabel} · Étape ${step.index}/${step.total} — ${step.title}`
    : typeLabel;
}

interface PriorityActionsProps {
  activeProcesses: Process[];
  tenants: Tenant[];
  onOpenProcess: (processId: Process["id"]) => void;
  onDeleteProcess: (processId: Process["id"]) => void | Promise<void>;
}

interface DisplayItem {
  key: string;
  categoryKey: string;
  action?: PriorityAction;
  process?: Process;
}

export default function PriorityActions({
  activeProcesses,
  tenants,
  onOpenProcess,
  onDeleteProcess,
}: PriorityActionsProps) {
  const nav = useNavigate();
  const { actions, createAction, snoozeAction, archiveAction, mutate } =
    usePriorityActions();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isArchivedOpen, setIsArchivedOpen] = useState(false);
  const [snoozeTarget, setSnoozeTarget] = useState<PriorityAction | null>(null);

  const visibleActions = actions.filter(
    (action) => !isSnoozed(action.remind_at),
  );
  const snoozedActions = actions.filter((action) =>
    isSnoozed(action.remind_at),
  );

  // Les processus de départ sans action fin_de_bail associée (données antérieures
  // au backfill) restent affichés comme avant.
  const linkedProcessIds = new Set(
    actions
      .filter((action) => action.type === "fin_de_bail")
      .map((action) => Number(action.meta?.id_process)),
  );
  const fallbackProcesses = activeProcesses.filter(
    (process) => !linkedProcessIds.has(Number(process.id)),
  );

  const items: DisplayItem[] = [
    ...visibleActions.map((action) => ({
      key: `action-${action.id}`,
      categoryKey: (TYPE_CONFIG[action.type] ?? TYPE_CONFIG.manual).category
        .key,
      action,
    })),
    ...fallbackProcesses.map((process) => ({
      key: `process-${process.id}`,
      categoryKey: CATEGORIES.fin_de_bail.key,
      process,
    })),
  ];

  const groups = CATEGORY_ORDER.map((categoryKey) => ({
    category: CATEGORIES[categoryKey],
    items: items.filter((item) => item.categoryKey === categoryKey),
  })).filter((group) => group.items.length > 0);

  function findTenant(tenantId: number | string | null | undefined) {
    return tenants.find((tenant) => tenant.id === Number(tenantId));
  }

  function openAction(action: PriorityAction) {
    const process = activeProcesses.find(
      (candidate) => Number(candidate.id) === action.meta?.id_process,
    );
    if (action.type === "fin_de_bail" && process) {
      onOpenProcess(process.id);
    } else if (action.type === "bank_sync") {
      nav("/bailleur/finances");
    } else if (action.type === "facture_config") {
      nav("/bailleur/profiles");
    } else if (action.type === "cautionnement") {
      nav(
        `/bailleur/tenants/${action.id_tenant}?tab-tenant-show=actions&action=caution`,
      );
    } else if (action.domaine === BalanceDomain.TENANT) {
      nav(`/bailleur/tenants/${action.id_tenant}`);
    } else if (action.domaine === BalanceDomain.PROPERTY) {
      nav(`/bailleur/properties/${action.id_bien}`);
    }
  }

  function hasTarget(action: PriorityAction) {
    return Boolean(
      action.id_tenant ||
        action.id_bien ||
        action.type === "bank_sync" ||
        (action.type === "fin_de_bail" && action.meta?.id_process),
    );
  }

  async function deleteAction(action: PriorityAction) {
    const process = activeProcesses.find(
      (candidate) => Number(candidate.id) === action.meta?.id_process,
    );
    if (action.type === "fin_de_bail" && process) {
      // La suppression du process archive aussi l'action côté serveur
      await onDeleteProcess(process.id);
      await mutate();
    } else {
      await archiveAction(action.id);
    }
  }

  function renderItem(item: DisplayItem) {
    if (item.process) {
      const tenant = findTenant(item.process.id_tenant);
      return (
        <PriorityActionItem
          key={item.key}
          icon={IconLogout}
          title={tenant?.nom ?? ""}
          subtitle={getProcessSubtitle(item.process, tenant)}
          onAction={() => onOpenProcess(item.process!.id)}
          onDelete={() => onDeleteProcess(item.process!.id)}
          actionTooltip="Continuer le processus"
          deleteTooltip="Supprimer le processus"
        />
      );
    }
    const action = item.action!;
    const process = activeProcesses.find(
      (candidate) => Number(candidate.id) === action.meta?.id_process,
    );
    const subtitle =
      action.type === "fin_de_bail" && process
        ? getProcessSubtitle(process, findTenant(action.id_tenant))
        : action.description ?? undefined;
    return (
      <PriorityActionItem
        key={item.key}
        icon={(TYPE_CONFIG[action.type] ?? TYPE_CONFIG.manual).icon}
        title={action.title}
        subtitle={subtitle}
        onAction={hasTarget(action) ? () => openAction(action) : undefined}
        onSnooze={() => setSnoozeTarget(action)}
        onDelete={() => deleteAction(action)}
        actionTooltip="Ouvrir"
        deleteTooltip="Archiver"
      />
    );
  }

  return (
    <Card padding="sm" className={styles.card}>
      <div className={styles.header}>
        <div className={styles.titleRow}>
          <Title variant="deca" weight="bold">
            Mes actions prioritaires
          </Title>
          {items.length > 0 && <CountBadge count={items.length} />}
        </div>
        <div className={styles.headerActions}>
          <Button
            variant="tertiary"
            size="sm"
            onClick={() => setIsArchivedOpen(true)}
          >
            Voir les archivées
          </Button>
          <Button
            variant="secondary"
            size="sm"
            icon={IconPlus}
            onClick={() => setIsFormOpen(true)}
          >
            Ajouter une action
          </Button>
        </div>
      </div>
      {items.length === 0 && (
        <Text color="muted">Aucune action prioritaire</Text>
      )}
      <div className={styles.list}>
        {groups.map((group) => (
          <div key={group.category.key} className={styles.category}>
            {group.items.length === 1 ? (
              <div className={styles.singleItem}>
                {renderItem(group.items[0])}
              </div>
            ) : (
              <CollapsibleCard
                variant="plain"
                titleWeight="regular"
                title={
                  <span className={styles.titleRow}>
                    {group.category.label}
                    <CountBadge count={group.items.length} />
                  </span>
                }
              >
                <div className={styles.panelContent}>
                  {group.items.map(renderItem)}
                </div>
              </CollapsibleCard>
            )}
          </div>
        ))}
        {snoozedActions.length > 0 && (
          <div className={styles.category}>
            <CollapsibleCard
              variant="plain"
              titleWeight="regular"
              title={
                <span className={styles.titleRow}>
                  En attente de rappel
                  <CountBadge count={snoozedActions.length} />
                </span>
              }
            >
              <div className={styles.panelContent}>
                {snoozedActions.map((action) => (
                  <PriorityActionItem
                    key={action.id}
                    icon={(TYPE_CONFIG[action.type] ?? TYPE_CONFIG.manual).icon}
                    title={action.title}
                    subtitle={`Rappel le ${moment(action.remind_at).format(
                      "DD/MM/YYYY",
                    )}${action.remind_by_mail ? " · avec mail de rappel" : ""}`}
                    onAction={
                      hasTarget(action) ? () => openAction(action) : undefined
                    }
                    onSnooze={() => setSnoozeTarget(action)}
                    onDelete={() => deleteAction(action)}
                    actionTooltip="Ouvrir"
                    snoozeTooltip="Modifier le rappel"
                    deleteTooltip="Archiver"
                  />
                ))}
              </div>
            </CollapsibleCard>
          </div>
        )}
      </div>
      <PriorityActionForm
        open={isFormOpen}
        onOpenChange={setIsFormOpen}
        onSubmit={async (payload) => {
          await createAction(payload);
        }}
      />
      <SnoozeDialog
        action={snoozeTarget}
        onOpenChange={(open) => !open && setSnoozeTarget(null)}
        onSnooze={snoozeAction}
      />
      <ArchivedActionsDialog
        open={isArchivedOpen}
        onOpenChange={setIsArchivedOpen}
        onRestored={() => mutate()}
      />
    </Card>
  );
}
